import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
} from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getProducts } from '../../../../appRedux/actions/Product';
import {
  messages,
  placeHolders,
  twoColumnsFormConfig,
  oneColumnFormConfig,
  dateFormatMx,
  label,
} from '../../../../constants/Forms';

import { CustomSelect, NumericInput } from '../../../uiComponents';
import { formatPrice } from '../../../../util/formatPrice';
import { medicalSaleProduct, consignOrderConst } from '../../../../constants';

export const ConsignmentOrderProducts = ({
  form = [],
  providerId = null,
  disabled = false,
}) => {
  const dispatch = useDispatch();
  const { products } = useSelector((state) => state.product);
  const filteredOptions = providerId
    ? products.filter(
        (p) => Array.isArray(p.orderType) && p.orderType.includes('consignment')
      )
    : [];

  const [productType, setProductType] = useState([]);

  const validateDuplicateAllotment =
    (fieldName, getFieldValue) => (_, value) => {
      if (!value) return Promise.resolve();

      const products = getFieldValue('products') || [];
      const currentProduct = products[fieldName]?.productId?.value;

      const isDuplicate = products.some(
        (item, idx) =>
          idx !== fieldName &&
          item?.productId?.value === currentProduct &&
          item?.allotment === value
      );

      if (isDuplicate) {
        return Promise.reject(new Error(consignOrderConst.duplicateAllotment));
      }

      return Promise.resolve();
    };

  useEffect(() => {
    if (providerId)
      dispatch(
        getProducts({
          providerId,
          status: '1',
          isApproved: '1',
          withDeleted: 'false',
        })
      );
  }, [dispatch, providerId]);

  const calculateSubtotal = (quantity, price) => {
    if (!quantity || !price) return '0.00';

    const q = parseFloat(quantity);
    const p = parseFloat(price);

    if (isNaN(q) || isNaN(p)) return '0.00';

    return formatPrice(q * p);
  };

  const handleQuantityOrPriceChange = (fieldIndex) => {
    const rows = form.getFieldValue('consignmentOrderProduct') || [];
    const row = rows[fieldIndex];

    if (row?.quantity && row?.price) {
      const subtotal = calculateSubtotal(row.quantity, row.price);
      form.setFieldValue(
        ['consignmentOrderProduct', fieldIndex, 'subtotal'],
        subtotal
      );
    }
  };

  const onProductChange = (product = null, data) => {
    const selectedProduct = products?.find(
      (item) => item?.id === product?.value
    );

    if (data) {
      setProductType((items) =>
        items.toSpliced(data?.index, 1, selectedProduct?.productType?.name)
      );
    }
  };

  const onRemoveProduct = (index) => {
    setProductType((items) => items.toSpliced(index, 1));
  };

  return (
    <>
      <Form.List name="consignmentOrderProduct">
        {(fields, { add, remove }) => (
          <div className="products-container">
            {fields.map((field) => (
              <>
                <Divider style={{ marginTop: 30 }} />

                <Row wrap>
                  <Col span={fields.length > 1 ? 23 : 24}>
                    <Form.Item
                      {...oneColumnFormConfig}
                      label="Producto"
                      name={[field.name, 'productId']}
                      rules={[
                        {
                          required: true,
                          message: messages.required('Producto'),
                        },
                      ]}
                    >
                      <CustomSelect
                        placeholder={placeHolders.product}
                        onChange={onProductChange}
                        labelInValue={true}
                        disabled={disabled}
                        options={filteredOptions.map(({ id, name, sku }) => ({
                          value: id,
                          label: `${sku} - ${name}`,
                          index: field.name,
                        }))}
                      />
                    </Form.Item>
                  </Col>

                  {fields.length > 1 && !disabled && (
                    <Col span={1}>
                      <MinusCircleOutlined
                        className="danger-button-product"
                        onClick={() => {
                          remove(field.name);
                          onProductChange();
                          onRemoveProduct(field.name);
                        }}
                      />
                    </Col>
                  )}
                </Row>

                <Row wrap>
                  <Col span={12}>
                    <Form.Item
                      {...twoColumnsFormConfig}
                      label="Cantidad"
                      name={[field.name, 'quantity']}
                      rules={[
                        {
                          required: true,
                          message: messages.required('Cantidad'),
                        },
                      ]}
                    >
                      <NumericInput
                        className="input-w100"
                        min={0}
                        disabled={disabled}
                        onChange={() => handleQuantityOrPriceChange(field.name)}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      {...twoColumnsFormConfig}
                      label="Precio"
                      name={[field.name, 'price']}
                      rules={[
                        {
                          required: true,
                          message: messages.required('Precio'),
                        },
                      ]}
                    >
                      <InputNumber
                        formatter={(value) =>
                          `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                        }
                        parser={(value) => value?.replace(/\$\s?|(,*)/g, '')}
                        stringMode={true}
                        disabled={disabled}
                        onChange={() => handleQuantityOrPriceChange(field.name)}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row wrap>
                  <Col span={12}>
                    <Form.Item
                      {...twoColumnsFormConfig}
                      label="Lote"
                      name={[field.name, 'allotment']}
                      rules={[
                        {
                          required:
                            productType[field.name] === medicalSaleProduct,
                          message: messages.required('Lote'),
                        },
                        ({ getFieldValue }) => ({
                          validator: validateDuplicateAllotment(
                            field.name,
                            getFieldValue
                          ),
                        }),
                      ]}
                    >
                      <Input className="input-w100" disabled={disabled} />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      {...twoColumnsFormConfig}
                      label={label.expiration}
                      name={[field.name, 'expiresAt']}
                      rules={[
                        {
                          required:
                            productType[field.name] === medicalSaleProduct,
                          message: messages.required(label.expiration),
                        },
                      ]}
                    >
                      <DatePicker
                        className="input-w100"
                        format={dateFormatMx}
                        disabled={disabled}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row wrap>
                  <Col span={12}>
                    <Form.Item
                      {...twoColumnsFormConfig}
                      label="Subtotal"
                      name={[field.name, 'subtotal']}
                    >
                      <Input
                        className="input-w100"
                        disabled={true}
                        value={calculateSubtotal(
                          form.getFieldValue([
                            'consignmentOrderProduct',
                            field.name,
                            'quantity',
                          ]),
                          form.getFieldValue([
                            'consignmentOrderProduct',
                            field.name,
                            'price',
                          ])
                        )}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            ))}

            <Button
              className="add-btn"
              disabled={disabled}
              onClick={() => {
                add();
                onProductChange();
              }}
              icon={<PlusOutlined />}
            >
              Agregar producto
            </Button>
          </div>
        )}
      </Form.List>
    </>
  );
};
